import React from 'react';
import c from 'classnames';
import { WithStyles, ITheme } from 'core/style/interfaces';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { button, buttonAccent, buttonShadow } from 'core/style';
import { shortDescLineHeight, titleLineHeight } from '../product-form/ProductFormCard';
import { Account, Lecturer } from 'site/graphql';
import { LinksConsumer } from 'core/components/linksContext';
import LecturerIcon from '../../svg/lecturer.svg';
import { isFilledArray, priceToString } from 'core/utils';
import separatedElements from 'core/components/common/separatedElements';

export type ITextbook = {
    lecturers: ({
        account: Pick<Account, 'id' | 'firstName' | 'lastName'>;
    } & Pick<Lecturer, 'id'>)[];
    title: string;
    subTitle: string;
    buttonTitle: string;
    externalLink: string;
    includes: string;
    pictureUrl: string;
    price: number;
};
type ITextbookProps = {
    textbook: ITextbook;
} & WithStyles<typeof styles>;

const Textbook: React.FC<ITextbookProps> = ({ classes, textbook }) => {
    return (
        <div className={classes.card}>
            <LinksConsumer>
                {links => (
                    <div
                        className={classes.media}
                        title={textbook.title}
                        style={{
                            background: `url(${require(`${textbook.pictureUrl}`)}) center center / cover no-repeat`,
                            backgroundColor: '#333333',
                        }}
                    >
                        <a className={classes.mediaLink} href={textbook.externalLink}>
                            {''}
                        </a>
                        <div className={classes.header}>
                            {isFilledArray(textbook.lecturers) && (
                                <div className={classes.lecturerWrap}>
                                    <LecturerIcon className={classes.lecturerIcon} />
                                    {separatedElements(textbook.lecturers, lecturer => (
                                        <links.teacherDetail.Link
                                            key={lecturer.id}
                                            title={lecturer.account.firstName + ' ' + lecturer.account.lastName}
                                            className={c(classes.lecturer, classes.lecturerLink)}
                                            to={links.teacherDetail.url(lecturer.id)}
                                        >
                                            {lecturer.account.firstName} {lecturer.account.lastName}
                                        </links.teacherDetail.Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </LinksConsumer>
            <div className={classes.content}>
                <div className={classes.descWrap}>
                    <a className={classes.title} href={textbook.externalLink}>
                        {textbook.title}
                    </a>
                    <p className={classes.shortDescription} title={textbook.subTitle}>
                        {textbook.subTitle}
                    </p>
                </div>
                <a href={textbook.externalLink} className={classes.more}>
                    Узнать больше
                </a>
                <div className={classes.includes}>
                    <span className={classes.includesTitle}>Включает в себя:</span>
                    <div className={classes.wrapper}>
                        <div className={classes.item}>
                            <p className={classes.text}>{textbook.includes}</p>
                        </div>
                    </div>
                </div>
                <div className={classes.infoOnMain}>
                    <div>
                        <span>
                            Стоимость:
                            <br />
                        </span>
                        <span className={classes.price}>
                            {textbook.price === 0 ? 'БЕСПЛАТНО' : priceToString(textbook.price)}
                        </span>
                    </div>
                </div>
                <div className={classes.wrapper}>
                    <a className={c(classes.pay, classes.content)} href={textbook.externalLink}>
                        {textbook.buttonTitle}
                    </a>
                </div>
            </div>
        </div>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        card: {
            flexGrow: 1,
            fontFamily: 'Arial, sans-serif',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.colors.lightBg,
            boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.27)',
            boxSizing: 'border-box',
            width: '100%',
        },
        cardDark: {
            backgroundColor: theme.colors.contrastBg,
            '& $descWrap:after, & $title:after': {
                background:
                    'linear-gradient(to bottom,' + 'rgba(30, 37, 47, 0)' + ' 0%, ' + theme.colors.contrastBg + ' 70%)',
                fallbacks: [
                    {
                        background:
                            '-moz-linear-gradient(top,' +
                            'rgba(30, 37, 47, 0)' +
                            ' 0%, ' +
                            theme.colors.contrastBg +
                            ' 70%)' /* FF3.6-15 */,
                    },
                    {
                        background:
                            '-webkit-linear-gradient(top,' +
                            'rgba(30, 37, 47, 0)' +
                            ' 0%, ' +
                            theme.colors.contrastBg +
                            ' 70%)' /* Chrome10-25,Safari5.1-6 */,
                    },
                ],
            },

            '& $title, & $content': {
                color: theme.colors.contrastBgText,
            },
            '& $title': {
                '&:hover, &:focus': {
                    color: theme.colors.contrastBgMedium,
                },
            },
            '& $content $lecturer': {
                color: theme.colors.contrastBgText,
            },
            '& $content': {
                borderBottom: '1px solid ' + theme.colors.contrastBgText,

                '&:hover, &:focus': {
                    color: theme.colors.contrastBgMedium,
                    borderBottomColor: theme.colors.contrastBgMedium,
                },
            },
            '& $includes': {
                color: theme.colors.contrastBgMedium,
            },
            '& $more': {
                color: theme.colors.accentLight,

                '&:hover, &:focus': {
                    color: theme.colors.secondary,
                    borderBottomColor: theme.colors.secondary,
                },
            },
        },
        media: {
            display: 'flex',
            alignItems: 'flex-end',
            height: 180,
            flexShrink: 100,
            backgroundColor: '#FFF',
            textDecoration: 'none',
            position: 'relative',
        },
        mediaLink: {
            position: 'absolute',
            left: 0,
            top: 0,
            height: 100 + '%',
            width: 100 + '%',
            zIndex: 0,
        },
        header: {
            flexGrow: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.65)',
            padding: '0.5rem 1.2rem',
            fontSize: 14,
            width: '100%',
            boxSizing: 'border-box',
            zIndex: 1,
        },
        lecturerWrap: {
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            maxWidth: '100%',
        },
        lecturerIcon: {
            display: 'inline-block',
            width: 0.8 + 'rem',
            height: 0.8 + 'rem',
            fill: theme.colors.accentLight,
            marginRight: 0.5 + 'rem',
        },
        lecturer: {
            textDecoration: 'none',
            color: theme.colors.textBase,
            lineHeight: 1.25 + 'rem',
            fontSize: 0.89 + 'rem',
        },
        lecturerLink: {
            '&:hover, &:focus': {
                color: theme.colors.textMedium,
            },
        },
        content: {
            padding: '1.2rem',
            paddingTop: '0.8rem',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            boxSizing: 'border-box',
            maxWidth: '100%',
            maxHeight: '100%',

            '& > *': {
                marginBottom: 0.5 + 'rem',
            },

            '& > :last-child': {
                marginBottom: 0,
            },
        },
        descWrap: {
            position: 'relative',
            marginBottom: 0,
            flexGrow: 1,
            height: titleLineHeight * 3.5 + shortDescLineHeight * 3.5 + 'rem',
            maxHeight: titleLineHeight * 3.5 + shortDescLineHeight * 3.5 + 'rem',
            overflow: 'hidden',
            paddingBottom: shortDescLineHeight / 2 + 'rem',

            '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -1,
                left: 0,
                width: '100%',
                height: shortDescLineHeight + 'rem',
                background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #FFF 70%)',
                fallbacks: [
                    {
                        background:
                            '-moz-linear-gradient(top, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 70%)' /* FF3.6-15 */,
                    },
                    {
                        background:
                            '-webkit-linear-gradient(top, rgba(255,255,255,0) 0%,rgba(255,255,255,1) 70%)' /* Chrome10-25,Safari5.1-6 */,
                    },
                ],
            },
        },
        title: {
            display: 'block',
            position: 'relative',
            fontFamily: 'Franklin Gothic Medium, Franklin Gothic, ITC Franklin Gothic, Arial, sans-serif',
            fontSize: 1.1 + 'rem',
            lineHeight: titleLineHeight + 'rem',
            color: theme.colors.textBase,
            textTransform: 'uppercase',
            textDecoration: 'none',
            maxHeight: titleLineHeight * 3.5 + 'rem',
            overflow: 'hidden',
            paddingBottom: titleLineHeight / 2 + 'rem',
            marginBottom: 0,

            '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -1,
                left: 0,
                width: '100%',
                height: titleLineHeight + 'rem',
                background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #FFF 70%)',
                fallbacks: [
                    {
                        background:
                            '-moz-linear-gradient(top, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 70%)' /* FF3.6-15 */,
                    },
                    {
                        background:
                            '-webkit-linear-gradient(top, rgba(255,255,255,0) 0%,rgba(255,255,255,1) 70%)' /* Chrome10-25,Safari5.1-6 */,
                    },
                ],
            },
        },
        shortDescription: {
            position: 'relative',
            fontSize: 0.89 + 'rem',
            flexGrow: 1,
            lineHeight: shortDescLineHeight + 'rem',
            marginBottom: 0,
        },
        includes: {
            color: theme.colors.textMedium,
            flexGrow: 1,
        },
        includesTitle: {
            fontSize: 0.89 + 'rem',
            fontWeight: 700,
        },
        more: {
            alignSelf: 'center',
            marginBottom: 0.5 + 'rem',
            fontSize: 0.89 + 'rem',
            lineHeight: 1.3,
            color: theme.colors.accent,
            textDecoration: 'none',

            '&:hover, &:focus': {
                color: theme.colors.secondary,
                borderBottomColor: theme.colors.secondary,
            },
        },
        info: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            fontSize: 0.7 + 'rem',
            lineHeight: 1.3,
        },
        price: {
            fontWeight: 700,
            fontSize: 1 + 'rem',
            lineHeight: 1.3 + 'rem',
            fontFamily: '"Helvetica Neue", sans-serif',
        },
        infoOnMain: {
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 0.7 + 'rem',
        },
        wrapper: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        pay: {
            ...button(theme),
            ...buttonAccent(theme),
            ...buttonShadow,
            fontSize: 1 + 'rem',
            padding: 0.75 + 'rem',
            fontWeight: 700,
        },
        item: {
            flex: '1 0 0',
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            marginBottom: 10,
        },
        text: {
            fontSize: 14,
            margin: '0 10px 0 0',
        },
    });

export default injectSheet(styles, 'Textbook')(Textbook);
